/**
 * BloccoRdaIntervento — componente condiviso
 *
 * Estratto da FaseAnalisi.tsx (renderMateriali, righe 288–452).
 * Mostra la sezione "Articoli RDA" in una Card, incluso:
 *   - lista articoli RDA (sistema vecchio)
 *   - note RDA in evidenza
 *   - badge per stato RDO
 *   - checkbox ricezione articoli RDO ordinati + DatePicker + salvataggio
 *
 * NOTA: il componente eredita dall'originale colori hex letterali
 * (es. #adc6ff, #fa8c16). Da allineare ai token Ant Design in un
 * commit separato — non toccati qui per "spostamento non riscrittura".
 */
import React from 'react'
import {
  Button, Card, Checkbox, DatePicker, Space, Tag, Tooltip, Typography, theme,
} from 'antd'
import { CheckCircleOutlined, SaveOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import type { Intervention } from '@/types'
import { usePermission } from '@/hooks/usePermission'

const { Text } = Typography

// ── Costanti RDO ──────────────────────────────────────────────────────────────
// Erano in FaseAnalisi.tsx (livello modulo) — spostate qui perché
// usate solo nella logica di questo blocco. Re-esportate perché
// FaseAnalisi le usa ancora in expandedRowKeys.
export const RDO_ORDINATA_STATUSES = ['ordinat', 'parzialmente ricevut', 'ricevut']
export const RDO_NOT_RECEIVABLE    = ['rifiutat', 'annullat']

// ── Props ─────────────────────────────────────────────────────────────────────
export interface BloccoRdaInterventoProps {
  record: Intervention
  interventionRdaMap: Record<number, {
    id: number
    note?: string | null
    items: { id: number; descrizione: string }[]
  }>
  rdaChecks: Record<number, Record<number, boolean>>
  rdaDates: Record<number, Record<number, ReturnType<typeof dayjs> | null>>
  savingRda: Record<number, boolean>
  onToggleItemCheck: (prId: number, itemId: number, checked: boolean) => void
  onDateChange: (prId: number, itemId: number, date: ReturnType<typeof dayjs> | null) => void
  onSaveRdaReceive: (pr: any) => void
}

// ── Componente ────────────────────────────────────────────────────────────────
const BloccoRdaIntervento: React.FC<BloccoRdaInterventoProps> = ({
  record,
  interventionRdaMap,
  rdaChecks,
  rdaDates,
  savingRda,
  onToggleItemCheck,
  onDateChange,
  onSaveRdaReceive,
}) => {
  const { token } = theme.useToken()
  // Il componente non riceve flag di permesso come prop (a differenza di
  // BloccoPreventiviOfficina): il gate sta qui, cosi' vale per entrambe le
  // schede che lo montano, la V2 e la V3. Il backend richiede lo stesso
  // permesso su PATCH /purchase-requests/{id}/items/{item}/receive.
  const canReceive = usePermission('purchase_requests.receive')

  // RDA — sistema vecchio (rdas/rda_items)
  const rdaData = record.id ? interventionRdaMap[record.id] : null
  const rdaItems = rdaData?.items ?? []

  // RDO — sistema nuovo (purchase_requests)
  const prList: any[] = (record as any).purchase_requests ?? []

  // RDO in stato ordinata+ per sezione checkbox
  const prOrdinati: any[] = prList.filter((pr: any) => {
    const nome = (pr.purchase_request_status_nome ?? '').toLowerCase()
    return RDO_ORDINATA_STATUSES.some(s => nome.includes(s))
  })

  if (rdaItems.length === 0 && prOrdinati.length === 0) return null

  // Conteggio RDO per stato
  const prByStatus = prList.reduce((acc: Record<string, number>, pr: any) => {
    const s = pr.purchase_request_status_nome ?? 'Sconosciuto'
    acc[s] = (acc[s] || 0) + 1
    return acc
  }, {})

  const rdaExtra = rdaItems.length > 0 && prList.length > 0
    ? (
      <Space size={2}>
        {Object.entries(prByStatus).map(([status, count]) => (
          <Tag key={status} color="blue" style={{ fontSize: 10, margin: 0, padding: '0 4px', lineHeight: '16px' }}>
            {count} RDO {status}
          </Tag>
        ))}
      </Space>
    )
    : null

  const content = (
    <div style={{ marginTop: 4, paddingLeft: 8, borderLeft: '2px solid #adc6ff' }}>
      {/* Sezione RDA con indicazione stato RDO */}
      {rdaItems.length > 0 && (
        <div style={{ marginBottom: prOrdinati.length > 0 ? 4 : 0 }}>
          {prList.length === 0 && (
            <Text style={{ fontSize: 10, color: '#fa8c16', fontStyle: 'italic', display: 'block', marginBottom: 2 }}>
              — RDO ancora da produrre
            </Text>
          )}
          {rdaItems.map((item: any, idx: number) => (
            <Text key={item.id ?? idx} type="secondary" style={{ fontSize: 11, display: 'block' }}>
              · {item.descrizione}
            </Text>
          ))}
          {rdaData?.note && (
            <div style={{
              marginTop: 6,
              padding: '6px 10px',
              background: token.colorWarningBg,
              border: `1px solid ${token.colorWarningBorder}`,
              borderRadius: token.borderRadiusSM,
              fontSize: 12,
              fontWeight: 700,
              color: token.colorWarning,
              lineHeight: 1.5,
            }}>
              📝 {rdaData.note}
            </div>
          )}
        </div>
      )}

      {/* RDO ordinate con checkbox ricezione + DatePicker */}
      {prOrdinati.map((pr: any) => {
        const statusNome = (pr.purchase_request_status_nome ?? '').toLowerCase()
        const isReceivable = !RDO_NOT_RECEIVABLE.some(s => statusNome.includes(s))
        const items: any[] = pr.items ?? []
        const itemChecks = rdaChecks[pr.id] ?? {}
        const itemDates = rdaDates[pr.id] ?? {}
        const hasNewChecks = items.some((item: any) => !item.ricevuto && itemChecks[item.id])
        const allReceived = items.length > 0 && items.every((item: any) => item.ricevuto)

        return (
          <div key={pr.id} style={{ marginBottom: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
              <Text style={{ fontSize: 10, fontWeight: 600, color: '#1677ff' }}>{pr.codice}</Text>
              <Tag
                color={allReceived ? 'success' : 'processing'}
                style={{ margin: 0, fontSize: 10, padding: '0 4px', lineHeight: '16px' }}
              >
                {pr.purchase_request_status_nome}
              </Tag>
              {allReceived && <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 11 }} />}
              {pr.fornitore && (
                <Text type="secondary" style={{ fontSize: 10 }}>{pr.fornitore}</Text>
              )}
            </div>
            {items.map((item: any) => (
              <div
                key={item.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '2px 0',
                  flexWrap: 'wrap',
                  background: item.ricevuto ? '#f6ffed' : itemChecks[item.id] ? '#e6f7ff' : 'transparent',
                  borderRadius: 3,
                }}
              >
                {item.ricevuto ? (
                  <Tooltip title={item.data_ricezione_item
                    ? `Ricevuto il ${new Date(item.data_ricezione_item).toLocaleDateString('it-IT')}`
                    : 'Già ricevuto'}>
                    <Checkbox checked disabled style={{ marginLeft: 2 }} />
                  </Tooltip>
                ) : isReceivable ? (
                  <Checkbox
                    checked={itemChecks[item.id] ?? false}
                    onChange={(e) => onToggleItemCheck(pr.id, item.id, e.target.checked)}
                    style={{ marginLeft: 2 }}
                  />
                ) : (
                  <Checkbox disabled style={{ marginLeft: 2 }} />
                )}
                <Text style={{ fontSize: 11 }}>{item.descrizione}</Text>
                <Text type="secondary" style={{ fontSize: 10 }}>×{item.quantita}</Text>
                {item.ricevuto && item.data_ricezione_item && (
                  <Text type="secondary" style={{ fontSize: 10, color: '#52c41a' }}>
                    ✓ {new Date(item.data_ricezione_item).toLocaleDateString('it-IT')}
                  </Text>
                )}
                {!item.ricevuto && isReceivable && itemChecks[item.id] && (
                  <DatePicker
                    size="small"
                    value={itemDates[item.id] ?? null}
                    onChange={(date) => onDateChange(pr.id, item.id, date)}
                    format="DD/MM/YYYY"
                    style={{ width: 110 }}
                    placeholder="Data ricezione"
                  />
                )}
              </div>
            ))}
            {isReceivable && hasNewChecks && canReceive && (
              <Button
                size="small"
                type="primary"
                icon={<SaveOutlined />}
                loading={savingRda[pr.id]}
                onClick={() => onSaveRdaReceive(pr)}
                style={{ marginTop: 4, fontSize: 11, height: 22 }}
              >
                Salva ricezione
              </Button>
            )}
          </div>
        )
      })}
    </div>
  )

  return (
    <Card
      size="small"
      title={<span style={{ fontSize: 12 }}>Articoli RDA</span>}
      extra={rdaExtra}
      styles={{ body: { padding: 8 } }}
    >
      {content}
    </Card>
  )
}

export default BloccoRdaIntervento
